import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import InvoiceDetailView from './InvoiceDetailView';
import { 
  Search, 
  Download, 
  Eye, 
  CheckCircle, 
  Clock, 
  XCircle, 
  FileText, 
  Printer,
  Calendar,
  DollarSign,
  Building2,
  FileX,
  MinusCircle
} from 'lucide-react';
import { toast } from 'sonner';

const monthlyDebitNotes = {
  '2025-10': [
    {
      number: 'DN-2024-0089',
      date: '2025-10-24',
      dueDate: '2025-11-24',
      client: 'Acme Corporation',
      relatedInvoice: 'INV-2024-1247',
      serviceProvider: 'ServicePro UAE',
      items: [
        { description: 'Service Adjustment - Overcharge', quantity: 1, unitPrice: 952.38, taxRate: 5, amount: 1000 }
      ],
      subtotal: 952.38,
      tax: 47.62,
      total: 1000,
      status: 'processed',
      ftaRef: 'FTA-2024-DN-089',
      erpSource: 'SAP',
      reason: 'Service overcharge correction'
    },
    {
      number: 'DN-2024-0088',
      date: '2025-10-22',
      dueDate: '2025-11-22',
      client: 'Tech Ventures LLC',
      relatedInvoice: 'INV-2024-1240',
      serviceProvider: 'ServicePro UAE',
      items: [
        { description: 'Additional Services Rendered', quantity: 2, unitPrice: 1190.48, taxRate: 5, amount: 2500 }
      ],
      subtotal: 2380.95,
      tax: 119.05,
      total: 2500,
      status: 'processed',
      ftaRef: 'FTA-2024-DN-088',
      erpSource: 'SAP',
      reason: 'Additional work performed'
    },
    {
      number: 'DN-2024-0087',
      date: '2025-10-20',
      dueDate: '2025-11-20',
      client: 'Global Trading FZ',
      relatedInvoice: 'INV-2024-1235',
      serviceProvider: 'ServicePro UAE',
      items: [
        { description: 'Price Adjustment', quantity: 1, unitPrice: 476.19, taxRate: 5, amount: 500 }
      ],
      subtotal: 476.19,
      tax: 23.81,
      total: 500,
      status: 'pending',
      ftaRef: 'Pending',
      erpSource: 'SAP',
      reason: 'Price increase notification'
    }
  ],
  '2025-09': [
    {
      number: 'DN-2024-0076',
      date: '2025-09-28',
      dueDate: '2025-10-28',
      client: 'Smart Solutions',
      relatedInvoice: 'INV-2024-1185',
      serviceProvider: 'ServicePro UAE',
      items: [
        { description: 'Late Fee Charge', quantity: 1, unitPrice: 238.10, taxRate: 5, amount: 250 }
      ],
      subtotal: 238.10,
      tax: 11.90,
      total: 250,
      status: 'processed',
      ftaRef: 'FTA-2024-DN-076',
      erpSource: 'SAP',
      reason: 'Late payment penalty'
    }
  ]
};

export default function SalesDebitNotes() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('2025-10');
  const [selectedNote, setSelectedNote] = useState<any>(null);
  const [isDetailView, setIsDetailView] = useState(false);

  const currentNotes = monthlyDebitNotes[selectedMonth as keyof typeof monthlyDebitNotes] || [];

  const filteredNotes = currentNotes.filter(note =>
    note.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.relatedInvoice.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'processed':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Processed
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleViewNote = (note: any) => {
    setSelectedNote(note);
    setIsDetailView(true);
  };

  const handleBackToList = () => {
    setIsDetailView(false);
    setSelectedNote(null);
  };

  const handleDownloadPDF = () => {
    toast.success('PDF download started');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    toast.success('Sales debit notes exported successfully');
  };

  const formatMonthDisplay = (month: string) => {
    const date = new Date(month + '-01');
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Show detail view if a note is selected
  if (isDetailView && selectedNote) {
    return (
      <InvoiceDetailView 
        invoice={selectedNote}
        onBack={handleBackToList}
        type="sales"
      />
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 bg-gradient-to-br from-slate-50 to-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg blur-md opacity-50"></div>
            <div className="relative p-2 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg premium-shadow">
              <MinusCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-3xl text-gray-900">Sales Debit Notes</h1>
            <p className="text-gray-600 mt-1">Manage debit notes issued for sales invoices</p>
          </div>
        </div>
        <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 shadow-lg" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Export Data
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-orange-500 shadow-sm hover:shadow-md transition-shadow hover-lift">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Debit Notes</p>
                <p className="text-3xl text-gray-900">{currentNotes.length}</p>
                <p className="text-xs text-gray-500 mt-2">{formatMonthDisplay(selectedMonth)}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <MinusCircle className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500 shadow-sm hover:shadow-md transition-shadow hover-lift">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                <p className="text-3xl text-gray-900">
                  {currentNotes.reduce((sum, note) => sum + note.total, 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-2">AED</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <DollarSign className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500 shadow-sm hover:shadow-md transition-shadow hover-lift">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Processed</p>
                <p className="text-3xl text-green-600">
                  {currentNotes.filter(note => note.status === 'processed').length}
                </p>
                <p className="text-xs text-green-600 mt-2">Successfully sent to FTA</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500 shadow-sm hover:shadow-md transition-shadow hover-lift">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Pending</p>
                <p className="text-3xl text-amber-600">
                  {currentNotes.filter(note => note.status === 'pending').length}
                </p>
                <p className="text-xs text-amber-600 mt-2">Awaiting processing</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="shadow-sm premium-shadow">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-1 relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by debit note number, client, or related invoice..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
            <div className="w-full md:w-64">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="h-11">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="flex-1 text-left">{formatMonthDisplay(selectedMonth)}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025-10">October 2025</SelectItem>
                  <SelectItem value="2025-09">September 2025</SelectItem>
                  <SelectItem value="2025-08">August 2025</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Debit Notes Table */}
      <Card className="shadow-sm premium-shadow hover-lift">
        <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-red-50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MinusCircle className="w-5 h-5 text-orange-600" />
                Sales Debit Notes - {formatMonthDisplay(selectedMonth)}
              </CardTitle>
              <CardDescription className="mt-1">
                Showing {filteredNotes.length} of {currentNotes.length} debit notes
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead>Debit Note #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Related Invoice</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Amount (AED)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNotes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2 text-gray-500">
                        <FileX className="w-12 h-12 text-gray-300" />
                        <p>No debit notes found</p>
                        <p className="text-sm">Try adjusting your search or filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredNotes.map((note) => (
                    <TableRow key={note.number} className="hover:bg-gray-50/50">
                      <TableCell className="text-orange-600">{note.number}</TableCell>
                      <TableCell className="text-gray-700">{note.date}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {note.relatedInvoice}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-900">{note.client}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 max-w-xs truncate">{note.reason}</TableCell>
                      <TableCell className="text-right text-gray-900">{note.total.toLocaleString()}</TableCell>
                      <TableCell>{getStatusBadge(note.status)}</TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-8 w-8 p-0"
                          onClick={() => handleViewNote(note)}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
